import { StudioNodeData, StudioNodeType } from '@common/studio-types';
import { Edge, useReactFlow } from 'reactflow';

type Args = {
  nodeId: string;
  /**
   * Number of nodes to traverse back to get the context
   */
  traverseNodesCount: number;
};

const isContextNode = (node: StudioNodeData) => {
  return [
    StudioNodeType.NarratorText,
    StudioNodeType.StatBlock,
    StudioNodeType.SingleSelect,
  ].includes(node.type);
};

export const useGetAiContext = () => {
  const reactFlow = useReactFlow();

  return (args: Args): StudioNodeData[] => {
    const { nodeId } = args;
    const sourceByTarget: Record<string, StudioNodeData> = {};
    const sourceHandleByTarget: Record<string, string> = {};
    const map: Record<string, StudioNodeData> = {};
    const count = { value: 0 };
    const nodes = reactFlow.getNodes();
    const edges = reactFlow.getEdges();

    nodes.forEach((node) => (map[node.id] = node.data));

    edges.forEach((edge: Edge) => {
      if (edge.sourceHandle?.startsWith('studio-')) {
        sourceHandleByTarget[edge.target] = edge.sourceHandle;
      }

      if (!sourceByTarget[edge.target]) {
        sourceByTarget[edge.target] = map[edge.source];
      }
    });

    let node = map[nodeId];
    const contextNodes: StudioNodeData[] = [];
    const visited = new Set<string>();

    do {
      const optionId = sourceHandleByTarget[node.id];

      node = sourceByTarget[node.id];

      if (node) {
        if (visited.has(node.id)) {
          break;
        }

        visited.add(node.id);

        if (isContextNode(node)) {
          if (node.type === StudioNodeType.SingleSelect) {
            contextNodes.push({
              ...node,
              options: node.options.map((option) => ({
                ...option,
                nextNodeId: optionId.includes(option.id) ? nodeId : '',
              })),
            });
          } else {
            contextNodes.push(node);
          }
          count.value++;
        }
      }
    } while (node && count.value < args.traverseNodesCount);

    return contextNodes;
  };
};
